import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
  useGetCurrentUserQuery,
  useUpdateUserProfileMutation,
} from "../../../api/users";
import { Card } from "../../../components/layout/Card";
import {
  ErrorWrapper,
  InputWrapper,
} from "../questionnaireInputs/SharedFormStyles";
import RequiredStar from "../../../components/RequiredAsterisk";
import { validatePhoneNumber } from "../../../utils/sharedValidators";
import { E164Number } from "../../../../../api/src/types";
import PhoneInputField from "../../../components/forms/PhoneInputField";
import { PhoneFieldContainer } from "../questionnaireInputs/PhoneField";
import { StyledDescription } from "../Questionnaire";
import { CardContainer } from "../Question";

const userPhoneNumberQuestionConfig = {
  title: "What is your phone number?",
  isRequired: true,
  description:
    "This will be used to verify your identity for online notarization. 🖋️",
};

const UserPhoneNumber = ({
  shouldValidateQuestionnaireSection = false,
}: {
  shouldValidateQuestionnaireSection?: boolean;
}) => {
  const { data: user } = useGetCurrentUserQuery();
  const [
    updateUser,
    { /*isLoading,*/ isError, /*isSuccess: isUpdateSuccess,*/ reset },
  ] = useUpdateUserProfileMutation();

  const [phoneInput, setPhoneInput] = useState<string>(user?.phoneNumber ?? "");
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setPhoneInput(user.phoneNumber ?? "");
    }
  }, [user]);

  const save = useDebouncedCallback((value: string | null) => {
    if (!user?.id) return;

    void updateUser({
      id: user.id,
      phoneNumber: value,
    });
  }, 1000);

  const handlePhoneChange = (value: E164Number | undefined) => {
    reset();
    const phoneValue = value ?? "";
    setPhoneInput(phoneValue);

    try {
      const error = validatePhoneNumber(phoneValue);
      setPhoneError(error);
      if (!phoneValue) {
        save(null);
        return;
      }
      if (!error) {
        save(phoneValue);
        return;
      }
    } catch (err) {
      console.error("Phone validation error:", err);
      setPhoneError("Error processing phone number");
    }
  };

  const validationError = !!(!phoneInput && shouldValidateQuestionnaireSection);
  //const isSuccess = isUpdateSuccess && !isLoading;

  return (
    <CardContainer>
      <Card
        header={{
          title: (
            <>
              {userPhoneNumberQuestionConfig.title}
              {userPhoneNumberQuestionConfig.isRequired && <RequiredStar />}
            </>
          ),
        }}
        isError={validationError}
      >
        {userPhoneNumberQuestionConfig.description && (
          <StyledDescription
            dangerouslySetInnerHTML={{
              __html: userPhoneNumberQuestionConfig.description,
            }}
          />
        )}
        <InputWrapper>
          <PhoneFieldContainer>
            <PhoneInputField
              value={phoneInput}
              handlePhoneChange={handlePhoneChange}
              placeholder="Enter phone number"
              errorMessage={phoneError}
              required={userPhoneNumberQuestionConfig.isRequired}
            />
          </PhoneFieldContainer>
          {/* Commenting out the loader and success icon for now as per the request of the design team */}
          {/*
          {isLoading && (
            <IconContainer>
              <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
            </IconContainer>
          )}
          {isSuccess && (
            <IconContainer>
              <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
            </IconContainer>
          )}
          */}
        </InputWrapper>

        {isError && (
          <ErrorWrapper>
            Error saving answer, please refresh the page and try again
          </ErrorWrapper>
        )}
      </Card>
    </CardContainer>
  );
};

export default UserPhoneNumber;
